import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Sticky from 'react-stickynode';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ReactNodeLike } from 'prop-types';
import { TopNav } from '@arcflight/tf-component-library';
import { useIntl } from 'react-intl';
import { Aircraft, StandardFieldConfig } from '../models/aircraft';
import { DashboardState } from '../models';
import { changeLayoutCollapsed } from '../models/menu';
import SwipeableDrawer from '../components/SwipeableDrawer';
import StatusBanner from '../components/StatusBanner/StatusBanner';
import useGetAircraft from '../services/hooks/aircraft/useGetAircraft';
import useQueryGetAllAircraft from '../services/hooks/aircraft/useQueryGetAllAircraft';
import useGetOperatorSetting from '../utils/useGetOperatorSetting';
import ProfileSettings from '../assets/topNav/profile-settings.svg';
import useGetAircraftStandardField from '../services/hooks/aircraft/useGetAircraftStandardField';
import useLogOut from '../utils/useLogOut';
import servers from '../utils/servers';
import LogoutIcon from '../assets/topNav/log-out.svg';
import planeIcon from '../assets/title-aircraft.svg';
import { getUserId } from '../cookies/userStorage';
import moreIcon from './assets/more-icon.svg';
import BasicLayout from './BasicLayout';
import AircraftDropdownNew from './menus/AircraftDropdownNew';
import AircraftMenuNew from './menus/AircraftMenuNew';
import aircraftMenuMap from './menus/useAircraftMenuMap';
import AircraftListForDropdown from './menus/AircraftListForDropdown';
import AircraftDropdownButton from './menus/AircraftDropdownButton';


export interface SelectedAircraftWithPath extends Aircraft {
  path: string;
  pathname: string;
  aircraftTitle: string;
}

const Wrapper = styled.div`
  display: flex;
  min-height: 0;
  max-width: 100vw;
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const SiderWrapper = styled.div`
  background-color: ${({ theme }) => theme.colours.contextMenuBlue};
  width: 200px;
  height: 100vh;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  @media (max-width: 767px) {
    display: none;
  }
`;

const TopNavWrapper = styled.div`
  flex: 1;
  margin-left: 200px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .sticky-outer-wrapper {
    flex-shrink: 0;
  }

  .sticky-inner-wrapper {
    z-index: 200;
  }

  @media (max-width: 767px) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: 24px 24px 0;
`;

const TopWrapper = styled.div`
  @media (max-width: 451px) {
    display: none;
  }
`;

const MobileDisplay = styled.div`
  display: none;
  @media (max-width: 451px) {
    display: block;
  }
`;

const Header = styled.div`
  background: ${({ theme }) => theme.colours.contextMenuBlue};
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const AircraftMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: 100%;
`;

const DropdownWrapper = styled.div`
  padding: 15px 20px;
`;

const StyledButton = styled.button`
  height: 72px;
  width: auto;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0 20px;
`;

const PageDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colours.brandAccentBlue};
  text-transform: capitalize;
  height: 72px;
  padding: 0 10px;
`;

const StyledImg = styled.img`
  width: 16px;
  margin-right: 15px;
`;

const InnerMenuLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handleLogOut = useLogOut();
  const [selectedAircraft, setSelectedAircraft] = useState<SelectedAircraftWithPath | undefined>();
  const [aircraftList, setAircraftList] = useState<SelectedAircraftWithPath[]>([]);
  const [localAircraft, setLocalAircraft] = useState<SelectedAircraftWithPath[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<NonNullable<ReactNodeLike>>(null);

  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const { userSettings } = useSelector((state: DashboardState) => state);

  const { data: aircraft } = useGetAircraft(id);
  const { data: allAircraft, isFetching } = useQueryGetAllAircraft();

  const intermittentFaultRename = useGetOperatorSetting('intermittent_fault_name_plural', id);
  const dentAndBuckleRename = useGetOperatorSetting('dent_and_buckle', id);
  const cabinIssueEnabled = useGetAircraftStandardField<StandardFieldConfig>('cabin_log', id)?.enabled;
  const cabinIssuesOverride = useGetOperatorSetting('cabin_issue_name_plural', id);
  const defectsNameOverride = useGetOperatorSetting('defect_name_plural', id);
  const notesToCrewOverride = useGetOperatorSetting('notes_to_crew_name', id);
  const workpackPluralOverride = useGetOperatorSetting('workpack_name_plural', id);
  const processApprovalMethodsOverride = useGetOperatorSetting('process_approval_methods', id);

  const getMenuItems = () => {
    return [
      {
        label: `${formatMessage({ id: 'menu.account.settings', defaultMessage: 'Profile Settings' })}`,
        icon: ProfileSettings,
        onSelect: () => {
          history.push('/settings/account');
        },
      },
      {
        label: `${formatMessage({ id: 'menu.account.logout' })}`,
        icon: LogoutIcon,
        onSelect: () => {
          handleLogOut();
        },
      },
    ]
  };

  const getAvatar = () => {
    return userSettings?.details?.avatar_url
      ? `${servers.api}/logins/${getUserId()}/avatar?${userSettings.details.avatar_url}`
      : null;
  };

  const drawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMobileAircraftMenuClick = () => {
    setDrawerContent(
      <AircraftMenuNew
        selectedKeys={[selectedAircraft?.pathname]}
        requiredSettings={{
          intermittentFaultRename,
          dentAndBuckleRename,
          aircraftId: id,
          cabinIssueEnabled,
          cabinIssuesOverride,
          defectsNameOverride,
          notesToCrewOverride,
          workpackPluralOverride,
          processApprovalMethodsOverride,
        }}
      />
    );
    drawerToggle();
  };

  const handleMobileDropdownClick = () => {
    setDrawerContent(
      <AircraftListForDropdown
        aircraft={localAircraft}
        aircraftLoading={isFetching}
        currentAircraftId={selectedAircraft?.id}
      />
    );
    drawerToggle();
  };


  const buildAircraftPathname = useMemo(() => (ac: Aircraft) => {
    const pathname = location.pathname.replace(`/aircraft/${id}/`, '');
    const slashIndex = pathname.indexOf('/');
    let commonPath = pathname.substring(0, slashIndex !== -1 ? slashIndex : pathname?.length);
    const commonPathRedirects = ['settings'];
    if (commonPathRedirects.includes(commonPath)) {
      commonPath = 'overview';
    }
    let aircraftTitle = ac.registration.substring(0, 8);
    if (ac.fleet_number) aircraftTitle = `${ac.fleet_number} (${ac.registration.substring(0, 8)})`;
    return {
      ...ac,
      path: `/aircraft/${ac.id}/${commonPath}`,
      pathname,
      aircraftTitle,
    };
  }, [id, location.pathname]);

  useEffect(() => {
    if (allAircraft) {
      const aircraftWithPath = allAircraft.map((ac) => {
        return buildAircraftPathname(ac);
      });
      setAircraftList(aircraftWithPath);
      setLocalAircraft(aircraftWithPath);
    }
  }, [allAircraft, id, location.pathname, buildAircraftPathname]);

  useEffect(() => {
    if (aircraft) {
      setSelectedAircraft(buildAircraftPathname(aircraft));
    }
  }, [aircraft, buildAircraftPathname]);

  useEffect(() => {
    dispatch(changeLayoutCollapsed({ payload: true }));
  }, [dispatch]);

  return (
    <BasicLayout>
      <Wrapper data-testid="InnerMenuLayout--Wrapper">
        <SiderWrapper data-testid="InnerMenuLayout--SiderWrapper">
          <AircraftDropdownNew
            selectedAircraft={selectedAircraft}
            aircraftLoading={isFetching}
            aircraftList={aircraftList}
          />
          <AircraftMenuNew
            selectedKeys={[selectedAircraft?.pathname]}
            requiredSettings={{
              intermittentFaultRename,
              dentAndBuckleRename,
              aircraftId: id,
              cabinIssueEnabled,
              cabinIssuesOverride,
              defectsNameOverride,
              notesToCrewOverride,
              workpackPluralOverride,
              processApprovalMethodsOverride,
            }}
          />
        </SiderWrapper>
        <TopNavWrapper data-testid="InnerMenuLayout--TopNavWrapper">
          <Sticky innerZ={200} top={0} id="stickyHeader">
            <TopWrapper data-testid="InnerMenuLayout--TopWrapper">
              <TopNav
                title={selectedAircraft?.aircraftTitle}
                titleIcon={planeIcon}
                isLoading={false}
                userRole={userSettings?.details?.people?.[0]?.type || '-'}
                username={
                  userSettings?.displayName?.length
                    ? userSettings?.displayName
                    : `${userSettings?.details?.first_name} ${userSettings?.details?.last_name}`
                }
                menuItems={getMenuItems()}
                userIcon={getAvatar()}
              />
              <StatusBanner
                status={selectedAircraft?.locked ? 'Locked' : selectedAircraft?.billing_status || 'Active'}
                crs={selectedAircraft?.maintenance_status_reason === 'missing_crs'}
                crsId={selectedAircraft?.missing_crs_workpack_id}
                aircraftId={selectedAircraft?.id}
              />
            </TopWrapper>
            <MobileDisplay data-testid="InnerMenuLayout--MobileDisplayHeader">
              <Header data-testid="InnerMenuLayout--Header">
                <DropdownWrapper>
                  <AircraftDropdownButton
                    handleDropdownClick={handleMobileDropdownClick}
                    selectedAircraft={selectedAircraft}
                  />
                </DropdownWrapper>
                <AircraftMenuWrapper>
                  <PageDisplay>
                    <StyledImg src={aircraftMenuMap.get(selectedAircraft?.pathname)?.icon} alt="" />
                    {aircraftMenuMap.get(selectedAircraft?.pathname)?.title}
                  </PageDisplay>
                  <StyledButton
                    type="button"
                    onClick={handleMobileAircraftMenuClick}
                    data-testid="InnerMenuLayout--MobileAircraftMenuButton"
                  >
                    <img src={moreIcon} alt="menu" />
                  </StyledButton>
                </AircraftMenuWrapper>
              </Header>
            </MobileDisplay>
          </Sticky>
          <ContentWrapper data-testid="InnerMenuLayout--ContentWrapper">
            {children}
          </ContentWrapper>
          <MobileDisplay data-testid="InnerMenuLayout--MobileDisplayDrawer">
            <SwipeableDrawer
              open={drawerOpen}
              onToggle={() => drawerToggle()}
              data-test="swipeableDrawer"
              swipeDrawerContent={drawerContent}
            />
          </MobileDisplay>
        </TopNavWrapper>
      </Wrapper>
    </BasicLayout>
  );
};

export default InnerMenuLayout;
